import { useEffect, useState } from "react";
import {
  RoomMemberEvent,
  UserEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
  type RoomMember,
  type User,
} from "matrix-js-sdk";

export interface PresenceInfo {
  presence: string; // "online" | "offline" | "unavailable"
  lastActiveAgoMs: number | null;
}

export interface PresenceTypingState {
  otherTyping: boolean;
  presence: PresenceInfo | null;
}

/**
 * Tracks the other user's presence and typing status for a 1:1 room.
 */
export function usePresenceTyping(
  client: MatrixClient,
  room: Room,
  otherUserId: string | null,
): PresenceTypingState {
  const [otherTyping, setOtherTyping] = useState(false);
  const [presence, setPresence] = useState<PresenceInfo | null>(null);

  useEffect(() => {
    setOtherTyping(false);

    const readPresence = () => {
      if (!otherUserId) {
        setPresence(null);
        return;
      }
      const user = client.getUser(otherUserId);
      if (!user) {
        setPresence(null);
        return;
      }
      setPresence({
        presence: user.presence ?? "offline",
        lastActiveAgoMs: user.lastActiveAgo ?? null,
      });
    };

    const onTyping = (_event: MatrixEvent, member: RoomMember) => {
      if (member.roomId !== room.roomId) return;
      if (member.userId !== otherUserId) return;
      setOtherTyping(Boolean(member.typing));
    };

    const onPresence = (_event: MatrixEvent | undefined, user: User) => {
      if (user.userId !== otherUserId) return;
      readPresence();
    };

    readPresence();
    client.on(RoomMemberEvent.Typing, onTyping);
    client.on(UserEvent.Presence, onPresence);

    return () => {
      client.removeListener(RoomMemberEvent.Typing, onTyping);
      client.removeListener(UserEvent.Presence, onPresence);
    };
  }, [client, room, otherUserId]);

  return { otherTyping, presence };
}
